import { useEffect, useMemo, useRef } from "react";
import "xterm/css/xterm.css";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { imagineStore } from "@/stores/imagine.store";
import { useQuery } from "@tanstack/react-query";
import { getSandboxServerLogs } from "@/lib/get-sandbox-server-logs";
import { getConsoleAuthToken } from "@/hooks/use-console-auth-token";

function useSandboxServerLogs() {
  const projectId = imagineStore.use.projectId();
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();

  const isTabActive = imagineStore.use.ui.canvasFooterOpen();
  return useQuery({
    enabled: !!projectId && sandboxInstrumented === true && isTabActive,
    queryKey: [projectId, "sandbox-server-logs"],
    // Only refetch in background if tab is open; disables interval otherwise
    refetchInterval: isTabActive ? 5000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: isTabActive,
    queryFn: async () => {
      const data = await getSandboxServerLogs({
        projectId: projectId!,
      });

      const logs = data.logs.split("\n").filter((line) => line.trim() !== "");
      return logs;
    },
  });
}

export function ServerLogsTab() {
  const sandboxServerLogsQuery = useSandboxServerLogs();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const wasAtBottomRef = useRef(true); // Track if user was at bottom before new content

  const logs = sandboxServerLogsQuery.data;

  // Auto scroll to bottom when new logs are added (only if user was at bottom)
  useEffect(() => {
    if (scrollContainerRef.current && logs && logs.length > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (scrollContainerRef.current && wasAtBottomRef.current) {
          scrollContainerRef.current.scrollTop =
            scrollContainerRef.current.scrollHeight;
        }
      });
    }
  }, [sandboxServerLogsQuery.data]);

  // Track scroll position to know if user is at bottom
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 10;
      wasAtBottomRef.current = isAtBottom;
    }
  };

  const renderRawLogSegment = (item: unknown) => {
    if (typeof item === "string") {
      return <span>{item}</span>;
    }

    if (typeof item === "object") {
      return <span>{JSON.stringify(item, null, 2)}</span>;
    }
  };

  return (
    <div className="h-full w-full bg-neutral-800 relative">
      <div
        ref={scrollContainerRef}
        className="h-full w-full overflow-y-scroll"
        onScroll={handleScroll}
      >
        {logs?.map((log: string, idx: number) => (
          <div
            key={idx}
            className={cn(
              `text-white font-mono text-sm hover:bg-neutral-700 px-3 py-1 w-full border-b border-neutral-700 text-[13px] whitespace-pre-wrap tracking-tight space-y-1.5`,
            )}
          >
            {renderRawLogSegment(log)}
          </div>
        ))}
      </div>
      <div className="absolute top-0 right-0 py-1.5 pr-5"></div>
    </div>
  );
}
